import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Monitor, Search, Plus, Edit, Loader2, LogOut,
  LayoutDashboard, Users, Megaphone, Calendar, CheckSquare, BarChart3, CreditCard,
  MapPin, Tv, Smartphone, Building
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { ParkWiseLogo } from "../../../components/ui/parkwise-logo";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../../components/ui/table";

const API = "/api";

const navItems = [
  { path: "/admin/advertising", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/advertising/advertisers", icon: Users, label: "Advertisers" },
  { path: "/admin/advertising/screens", icon: Monitor, label: "Screens & Inventory" },
  { path: "/admin/advertising/campaigns", icon: Megaphone, label: "Campaigns" },
  { path: "/admin/advertising/calendar", icon: Calendar, label: "Bookings Calendar" },
  { path: "/admin/advertising/approvals", icon: CheckSquare, label: "Artwork Approvals" },
  { path: "/admin/advertising/proof-of-play", icon: BarChart3, label: "Proof of Play" },
  { path: "/admin/advertising/billing", icon: CreditCard, label: "Billing & Invoices" },
];

const screenTypeIcons = {
  kiosk: Tv,
  internal_display: Monitor,
  retailer_facing: Smartphone,
  roadside: Building,
  led_board: Tv,
  totem: Building,
  waiting_area: Monitor,
  window_display: Tv
};

const screenTypeLabels = {
  kiosk: "Kiosk",
  internal_display: "Internal Display",
  retailer_facing: "Retailer Facing",
  roadside: "Roadside",
  led_board: "LED Board",
  totem: "Totem",
  waiting_area: "Waiting Area",
  window_display: "Window Display"
};

const statusColors = {
  active: "bg-green-600",
  inactive: "bg-gray-600",
  maintenance: "bg-yellow-600",
  offline: "bg-red-600"
};

export default function AdvertisingScreens() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => { checkAuth(); }, []);
  useEffect(() => { if (user) fetchScreens(); }, [user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) { navigate("/admin/login"); }
  };

  const fetchScreens = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/advertising/screens`, { withCredentials: true });
      setScreens(response.data);
    } catch (error) {
      console.error("Failed to fetch screens:", error);
    } finally { setLoading(false); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) {}
    navigate("/admin/login");
  };

  if (!user) {
    return (<div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>);
  }

  const filteredScreens = screens.filter(s => 
    !search || s.name?.toLowerCase().includes(search.toLowerCase()) || s.site_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside className="w-72 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <ParkWiseLogo size="md" />
          <p className="text-slate-500 text-xs mt-2">Advertising & Campaigns</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? "bg-blue-600/20 text-blue-400" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}>
                <item.icon className="w-5 h-5" /><span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
          <div className="pt-4 border-t border-slate-800 mt-4">
            <button onClick={() => navigate("/admin/dashboard")} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800">
              <LayoutDashboard className="w-5 h-5" /><span className="text-sm">← Back to Main Admin</span>
            </button>
          </div>
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3 px-2">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full px-2">
            <LogOut className="w-4 h-4" /><span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-white">Screens & Inventory</h1>
              <p className="text-slate-400 mt-1">Manage advertising screens and display inventory</p>
            </div>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-screen-btn">
              <Plus className="w-4 h-4 mr-2" />Add Screen
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Total Screens</p>
                    <p className="text-2xl font-bold text-white">{screens.length}</p>
                  </div>
                  <Monitor className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Active</p>
                    <p className="text-2xl font-bold text-green-400">{screens.filter(s => s.status === 'active').length}</p>
                  </div>
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Offline</p>
                    <p className="text-2xl font-bold text-red-400">{screens.filter(s => s.status === 'offline').length}</p>
                  </div>
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Screen Types</p>
                    <p className="text-2xl font-bold text-white">{new Set(screens.map(s => s.screen_type)).size}</p>
                  </div>
                  <Tv className="w-8 h-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <div className="mb-6 flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search screens..." className="pl-10 bg-slate-900 border-slate-700 text-white" />
            </div>
          </div>

          {/* Screens Table */}
          <Card className="bg-slate-900 border-slate-800">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>
            ) : filteredScreens.length === 0 ? (
              <CardContent className="py-12 text-center">
                <Monitor className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No screens found. Add your first screen to start managing inventory.</p>
              </CardContent>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Screen</TableHead>
                    <TableHead className="text-slate-400">Type</TableHead>
                    <TableHead className="text-slate-400">Site</TableHead>
                    <TableHead className="text-slate-400">Resolution</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredScreens.map(screen => {
                    const TypeIcon = screenTypeIcons[screen.screen_type] || Monitor;
                    return (
                      <TableRow key={screen.screen_id} className="border-slate-800 hover:bg-slate-800/50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                              <TypeIcon className="w-5 h-5 text-slate-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{screen.name}</p>
                              <p className="text-xs text-slate-500">{screen.screen_id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-300">{screenTypeLabels[screen.screen_type] || screen.screen_type}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-500" />
                            <span className="text-slate-300">{screen.site_name || screen.site_id}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-400">{screen.resolution || "1920x1080"}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs text-white ${statusColors[screen.status] || 'bg-slate-600'}`}>
                            {screen.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
